// Versão: 1.00
// Data..: 2026-10-13
// Editor da base de conhecimento baseado em Tiptap (ProseMirror).
// Mantém a mesma interface do RichEditor (value HTML / onChange) para troca sem fricção,
// mas reconhece sintaxe Markdown ao digitar/colar (ex.: "## ", "**texto**", "- ", "> ",
// "`código`") graças às input rules do StarterKit. O conteúdo é armazenado como HTML,
// então o backend (sanitizeHtml) e a renderização (dangerouslySetInnerHTML) não mudam.

import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { uploadFile, uploadPublicImage } from '../lib/upload.js';
import { useDialog } from '../context/DialogContext.js';
import { config } from '../config.js';

interface MarkdownEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  // Quando true, usa o endpoint de upload público (portal sem login).
  publicUpload?: boolean;
  // Disparado ao pressionar Ctrl+Enter (ou ⌘+Enter). Usado para enviar a resposta.
  onSubmit?: () => void;
}

// Monta a URL pública da imagem a partir da chave S3, no mesmo padrão do RichEditor:
// a chave (que contém "/") é codificada em base64url para caber num único segmento.
function attachmentUrl(s3Key: string): string {
  const encoded = btoa(s3Key).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${config.apiUrl}/public/attachments/${encoded}`;
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minHeight = 140,
  publicUpload = false,
  onSubmit,
}: MarkdownEditorProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { prompt } = useDialog();

  // onSubmit pode mudar entre renders; guardamos numa ref para o handler de tecla
  // sempre enxergar a versão atual sem recriar o editor.
  const submitRef = useRef(onSubmit);
  useEffect(() => {
    submitRef.current = onSubmit;
  }, [onSubmit]);

  const editor = useEditor({
    extensions: [
      // StarterKit já traz as input rules de Markdown (títulos, negrito, itálico,
      // listas, citação, código e bloco de código) ao digitar a sintaxe.
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: false }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'rich-body',
        style: `min-height:${minHeight}px;padding:12px;outline:none;font-size:14px;line-height:1.6`,
        'data-placeholder': placeholder ?? '',
      },
      handlePaste: (_view, event) => {
        const image = Array.from(event.clipboardData?.items ?? []).find((i) =>
          i.type.startsWith('image/'),
        );
        if (image) {
          const file = image.getAsFile();
          if (file) {
            event.preventDefault();
            void insertImage(file);
            return true;
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const file = Array.from(event.dataTransfer?.files ?? []).find((f) =>
          f.type.startsWith('image/'),
        );
        if (file) {
          event.preventDefault();
          void insertImage(file);
          return true;
        }
        return false;
      },
      handleKeyDown: (_view, event) => {
        // Ctrl+Enter (ou ⌘+Enter) envia — atalho de resposta rápida.
        if (submitRef.current && event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          submitRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Sincroniza o valor externo apenas quando difere (evita resetar o cursor a cada tecla).
  useEffect(() => {
    if (editor && !editor.isDestroyed && editor.getHTML() !== value) {
      // Tiptap v2: setContent(content, emitUpdate?, parseOptions?). false = não dispara onUpdate.
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  // Faz upload de uma imagem e insere no ponto do cursor.
  async function insertImage(file: File) {
    if (!editor) return;
    setUploading(true);
    try {
      const att = publicUpload ? await uploadPublicImage(file) : await uploadFile(file);
      editor.chain().focus().setImage({ src: attachmentUrl(att.s3Key), alt: att.fileName }).run();
    } finally {
      setUploading(false);
    }
  }

  // Insere/edita um link a partir da seleção atual (pede a URL num modal da aplicação).
  async function insertLink() {
    if (!editor) return;
    const previous = (editor.getAttributes('link').href as string | undefined) ?? 'https://';
    const url = await prompt({
      title: 'Inserir link',
      label: 'Endereço (URL)',
      defaultValue: previous,
      placeholder: 'https://exemplo.com',
      confirmLabel: 'Inserir',
    });
    // null = cancelou; string vazia = remover o link.
    if (url === null) return;
    const chain = editor.chain().focus();
    if (url === '') {
      chain.extendMarkRange('link').unsetLink().run();
    } else {
      chain.extendMarkRange('link').setLink({ href: url }).run();
    }
  }

  if (!editor) return null;

  return (
    <div
      style={{
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
      }}
    >
      <div
        className="flex items-center gap-1"
        style={{
          padding: '6px 8px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface-2)',
          flexWrap: 'wrap',
        }}
      >
        <Btn title="Negrito" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} style={{ fontWeight: 700 }}>B</Btn>
        <Btn title="Itálico" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} style={{ fontStyle: 'italic' }}>I</Btn>
        <Btn title="Riscado" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} style={{ textDecoration: 'line-through' }}>S</Btn>
        <Sep />
        <Btn title="Título" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H</Btn>
        <Btn title="Texto normal" active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>¶</Btn>
        <Btn title="Citação" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</Btn>
        <Sep />
        <Btn title="Lista com marcadores" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</Btn>
        <Btn title="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</Btn>
        <Sep />
        <Btn title="Inserir link" active={editor.isActive('link')} onClick={insertLink}>🔗</Btn>
        <Btn title="Bloco de código" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>{'</>'}</Btn>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void insertImage(f);
            e.target.value = '';
          }}
        />
        <Btn title="Inserir imagem" onClick={() => fileRef.current?.click()}>🖼</Btn>
        {uploading && <span className="spinner" style={{ width: 14, height: 14, marginLeft: 6 }} />}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

// Botão da barra de ferramentas (realça quando a marca/nó está ativo no cursor).
function Btn({
  onClick,
  title,
  children,
  style,
  active,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-ghost'}`}
      style={{ minWidth: 32, height: 30, padding: '0 6px', ...style }}
    >
      {children}
    </button>
  );
}

const Sep = () => (
  <span style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 3px' }} />
);
